/**
 * ProfileCompletionStrip — §7.17 / DEV-1484 last-mile guidance.
 * Mockup of record: `profile-completion-mockup.html` (repo root, 3 states).
 *
 * Own-profile + incomplete ONLY (host enforces): "Complete your profile —
 * n of 3" with per-item ✓/○ chips; a chip click scrolls to and focuses its
 * element (host provides the focusers). When the LAST item completes in
 * this session, the strip resolves to the finish moment — "Profile
 * complete — your Roots grew a little just now" + "Back to Action Center —
 * N tasks waiting" where N is the LIVE shared-composer badge count (§7.14:
 * the link and the nav badge read one source and can never disagree); at
 * zero, the all-caught-up line. Then the apparatus never renders again.
 * No confetti (locked).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { ProfileCompletionState } from '../../Data/Onboarding/profileCompletion';
import styles from './ProfileCompletionStrip.module.css';

export type ProfileItemKey = 'photo' | 'cover' | 'bio';

interface Props {
  state: ProfileCompletionState;
  /** True when the profile reached complete DURING this visit — renders
   *  the finish moment instead of disappearing instantly. */
  justCompleted: boolean;
  /** LIVE Action Center badge count (App's §7.14 shared composer value). */
  acBadgeCount: number;
  /** Scroll-and-focus the element for a chip (host wires the targets). */
  onFocusItem: (item: ProfileItemKey) => void;
}

const ProfileCompletionStrip: React.FC<Props> = ({
  state,
  justCompleted,
  acBadgeCount,
  onFocusItem,
}) => {
  const { t } = useAppTranslation('userProfile');
  const navigate = useNavigate();

  // Post-completion (not just now) → nothing, forever.
  if (state.complete && !justCompleted) return null;

  // ── Finish moment ──
  if (state.complete && justCompleted) {
    return (
      <div className={`${styles.strip} ${styles.stripDone}`} role="status">
        <span className={styles.doneText}>
          ✓ {t('completion.finish')}
        </span>
        {acBadgeCount > 0 ? (
          <button
            type="button"
            className={styles.backLink}
            onClick={() => navigate('/actions')}
          >
            {t('completion.backToAc', { count: acBadgeCount })} →
          </button>
        ) : (
          <span className={styles.allCaughtUp}>{t('completion.allCaughtUp')}</span>
        )}
      </div>
    );
  }

  // ── Incomplete: title + chips + n-of-3 ──
  const chips: Array<{ key: ProfileItemKey; done: boolean; label: string }> = [
    { key: 'photo', done: state.photo, label: t('completion.chipPhoto') },
    { key: 'cover', done: state.cover, label: t('completion.chipCover') },
    { key: 'bio', done: state.bio, label: t('completion.chipBio') },
  ];

  return (
    <div className={styles.strip} role="status">
      <span className={styles.title}>{t('completion.title')}</span>
      <div className={styles.chips}>
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`${styles.chip} ${c.done ? styles.chipDone : ''}`}
            onClick={() => onFocusItem(c.key)}
            aria-label={
              c.done
                ? t('completion.chipDoneAria', { item: c.label })
                : t('completion.chipTodoAria', { item: c.label })
            }
          >
            <span className={styles.chipMark} aria-hidden="true">
              {c.done ? '✓' : '○'}
            </span>
            {c.label}
          </button>
        ))}
      </div>
      <span className={styles.count}>
        {t('completion.countDone', { done: state.doneCount })}
      </span>
    </div>
  );
};

export default ProfileCompletionStrip;
